import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, X, TrendingUp, Upload, FileText, Eye, Edit2, Wallet, Building2, Users, Sparkles, Camera, DollarSign } from 'lucide-react';
import ExpenseCameraCapture from '../components/ExpenseCameraCapture';

/**
 * Inline Autopilot suggestion banner for the Expense Type select.
 * Maps the canonical Intelligence categories (Fuel, Hotel, Meals, …)
 * to the closest option in this form's hard-coded dropdown, then
 * surfaces a one-click Accept pill.
 *
 * Form↔Intelligence vocabulary mapping — keeps the engine independent
 * of any one UI's option list.
 */
const _AI_TO_FORM_TYPE = {
  Fuel:             'Transportation',
  Transportation:   'Transportation',
  Hotel:            'Other',
  Meals:            'Other',
  'Office Supplies':'Office Supplies',
  'Vehicle Service':'Maintenance',
  Telecom:          'Utilities',
  Utilities:        'Utilities',
  Parking:          'Transportation',
  Subscriptions:    'Other',
  Training:         'Other',
  Other:            'Other',
};

function ExpenseTypeAISuggestion({ vendor, description, currentValue, onAccept }) {
  const [suggestion, setSuggestion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const debounceRef = useRef(null);
  const sentFeedbackRef = useRef(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (currentValue) { setSuggestion(null); return; }
    const hasSignal = (vendor || '').trim().length >= 2 || (description || '').trim().length >= 2;
    if (!hasSignal) { setSuggestion(null); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.post('/intelligence/suggest', {
          classifiers: ['expense_category'],
          context: { vendor, description },
        });
        const s = (data?.suggestions || []).find(x => x.classifier === 'expense_category');
        if (s && s.data && (s.tier === 'suggest' || s.tier === 'auto_fill')) {
          setSuggestion(s);
          sentFeedbackRef.current = false;
        } else {
          setSuggestion(null);
        }
      } catch (_) { setSuggestion(null); }
    }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [vendor, description, currentValue]);

  const fireFeedback = (action, finalVal) => {
    if (!suggestion || sentFeedbackRef.current) return;
    sentFeedbackRef.current = true;
    axiosInstance.post('/intelligence/feedback', {
      classifier_type: 'expense_category',
      input: { vendor, description },
      suggestion: suggestion.data,
      confidence: suggestion.confidence,
      tier: suggestion.tier,
      user_action: action,
      user_value: finalVal,
    }).catch(() => {});
  };

  if (!suggestion || dismissed) return null;
  const aiCategory = suggestion.data.category;
  const mappedForm = _AI_TO_FORM_TYPE[aiCategory] || 'Other';
  const confPct = Math.round(suggestion.confidence * 100);

  return (
    <div
      data-testid="expense-type-ai-suggestion"
      className="mb-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs bg-emerald-50 border border-emerald-200 text-emerald-900"
    >
      <Sparkles size={12} className="text-emerald-600" />
      <span>
        AI suggests: <span className="font-semibold">{mappedForm}</span>
        {aiCategory !== mappedForm && (
          <span className="text-emerald-700 opacity-70"> (from &quot;{aiCategory}&quot;)</span>
        )}
        <span className="text-emerald-600 ml-1">· {confPct}%</span>
      </span>
      <button
        type="button"
        data-testid="ai-suggest-accept"
        onClick={() => { onAccept(mappedForm); fireFeedback('accept', mappedForm); setDismissed(true); }}
        className="text-emerald-700 hover:text-emerald-900 font-semibold underline"
      >
        Accept
      </button>
      <button
        type="button"
        data-testid="ai-suggest-dismiss"
        onClick={() => { fireFeedback('reject', ''); setDismissed(true); }}
        className="text-emerald-600 hover:text-emerald-800"
        aria-label="Dismiss"
        title="Hub stays sole writer · advisory only"
      >
        <X size={12} />
      </button>
    </div>
  );
}

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pettyCashFunds, setPettyCashFunds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);   // Iter-145 camera capture
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    expense_date: '',
    expense_from: '',
    expense_no: '',
    supplier_vat_number: '',
    expense_type: '',
    amount: '',
    vat: 0,
    total_with_vat: 0,
    description: '',
    project_id: '',
    photo_url: '',
    payment_source: 'cash',
    petty_cash_fund_id: '',
    cash_account_id: '',
    employee_id: ''
  });

  // Legacy 'bank' payment-source repair (Jun 2026)
  const [bankAudit, setBankAudit] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [repairBankId, setRepairBankId] = useState('');
  const [repairing, setRepairing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, projectsRes, fundsRes, employeesRes, accountsRes] = await Promise.all([
        // Main Expenses page hides rows that are already reported / journalized
        // by default (matches the user's mental model: once an expense has been
        // moved into a report and posted, it's "done" and shouldn't clutter the
        // working set). Use the dedicated audit view to see the full history.
        axiosInstance.get('/expenses'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/petty-cash/funds'),
        axiosInstance.get('/employees'),
        axiosInstance.get('/accounts')
      ]);
      setExpenses(expensesRes.data);
      setProjects(projectsRes.data);
      setPettyCashFunds((fundsRes.data || []).filter(f => f.status === 'active'));
      setEmployees((employeesRes.data || []).filter(e => e.approval_status === 'approved'));
      // Only Asset accounts explicitly flagged is_cash_account=true qualify
      // as a valid CR side for a cash expense. This matches the backend
      // guard in POST /expenses (payment_source='cash').
      setCashAccounts(
        (accountsRes.data || []).filter(a =>
          a.account_type === 'assets' && a.is_cash_account === true && a.status !== 'inactive'
        )
      );
      try {
        const [auditRes, banksRes] = await Promise.all([
          axiosInstance.get('/expenses-tools/bank-source-audit'),
          axiosInstance.get('/bank/accounts'),
        ]);
        setBankAudit(auditRes.data);
        setBankAccounts((banksRes.data || []).filter(b => b.status === 'active'));
      } catch (e) { /* non-blocking */ }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (amount, vat) => {
    const amountVal = parseFloat(amount) || 0;
    const vatVal = parseFloat(vat) || 0;
    return amountVal + vatVal;
  };

  const handleAmountChange = (value) => {
    const amount = parseFloat(value) || 0;
    const total = calculateTotal(amount, formData.vat);
    setFormData({ ...formData, amount: value, total_with_vat: total });
  };

  const handleVatChange = (value) => {
    const vat = parseFloat(value) || 0;
    const total = calculateTotal(formData.amount, vat);
    setFormData({ ...formData, vat: value, total_with_vat: total });
  };

  // Auto-fill VAT Number + Expense Type when the user types (or blurs)
  // a vendor name that matches a previously recorded expense.  Values
  // remain editable — this is a productivity shortcut, not a hard bind.
  const autofillFromVendor = (rawVendor) => {
    const v = (rawVendor || '').trim().toLowerCase();
    if (!v) return;
    // Find the MOST RECENT expense from this vendor (case-insensitive).
    const match = [...expenses]
      .filter(x => (x.expense_from || '').trim().toLowerCase() === v)
      .sort((a, b) =>
        String(b.expense_date || b.created_at || '').localeCompare(
          String(a.expense_date || a.created_at || '')
        )
      )[0];
    if (!match) return;
    setFormData((prev) => ({
      ...prev,
      // Only fill blanks — never overwrite what the user already typed.
      supplier_vat_number: prev.supplier_vat_number || match.supplier_vat_number || '',
      expense_type:        prev.expense_type        || match.expense_type        || '',
    }));
  };

  // Locate the canonical "11150 · Cash Expenses" account (fall back to
  // system_code=CASH_EXPENSES or any is_cash_account whose number
  // starts with 111 if the exact 11150 isn't seeded on this tenant).
  const findDefaultCashExpenseAccount = () => {
    if (!cashAccounts || cashAccounts.length === 0) return null;
    return (
      cashAccounts.find(a => a.account_number === '11150')
      || cashAccounts.find(a => (a.system_code || '').toUpperCase() === 'CASH_EXPENSES')
      || cashAccounts.find(a => (a.account_number || '').startsWith('111'))
      || null
    );
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - allow images and PDF
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Invalid file type. Only JPEG, PNG, and PDF are allowed.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size too large. Maximum 10MB allowed.');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axiosInstance.post('/expenses/upload-photo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, photo_url: response.data.photo_url });
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleViewImage = (fileUrl) => {
    if (!fileUrl) return;
    
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${fileUrl}`;
    
    // Check if it's a PDF - open in new tab
    if (fileUrl.toLowerCase().endsWith('.pdf')) {
      // Try to open in new window
      const newWindow = window.open(fullUrl, '_blank');
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Popup blocked - show alternative
        toast.info('Popup blocked. Click the link to view PDF.');
        // Create a temporary link
        const link = document.createElement('a');
        link.href = fullUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      return;
    }
    // For images, show in modal
    setSelectedImage(fileUrl);
    setShowImageModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Normalise optional FKs: convert '' → null so the backend receives an
      // explicit absence (matches the locked architectural rule:
      // 'No Project' = null, never an empty string).
      const payload = {
        ...formData,
        project_id:        formData.project_id        || null,
        petty_cash_fund_id: formData.petty_cash_fund_id || null,
        cash_account_id:   formData.cash_account_id    || null,
        employee_id:       formData.employee_id       || null,
      };
      if (editingExpense) {
        // Update existing expense
        await axiosInstance.put(`/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated successfully');
      } else {
        // Create new expense
        await axiosInstance.post('/expenses', payload);
        toast.success(
          payload.project_id
            ? 'Expense added successfully'
            : 'General expense added (no project allocation)',
        );
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : '',
      expense_from: expense.expense_from || '',
      expense_no: expense.expense_no || '',
      supplier_vat_number: expense.supplier_vat_number || '',
      expense_type: expense.expense_type || '',
      amount: expense.amount || '',
      vat: expense.vat || 0,
      total_with_vat: expense.total_with_vat || 0,
      description: expense.description || '',
      project_id: expense.project_id || '',
      photo_url: expense.photo_url || '',
      payment_source: expense.payment_source === 'bank' ? 'cash' : (expense.payment_source || 'cash'),
      petty_cash_fund_id: expense.petty_cash_fund_id || '',
      cash_account_id: expense.cash_account_id || '',
      employee_id: expense.employee_id || ''
    });
    setShowModal(true);
  };

  const handleBankSourceRepair = async () => {
    if (!repairBankId) return toast.error('اختر حساب البنك أولاً');
    if (!window.confirm(`سيتم تصحيح ${bankAudit?.count} مصروف: استرجاع من العهدة وخصم من البنك المحدد + إظهارها في كشف حركات البنك. متابعة؟`)) return;
    setRepairing(true);
    try {
      const res = await axiosInstance.post('/expenses-tools/bank-source-repair', {
        bank_account_id: repairBankId,
      });
      toast.success(res.data?.message_ar || 'تم التصحيح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message_ar || 'فشل التصحيح');
    } finally {
      setRepairing(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await axiosInstance.delete(`/expenses/${id}`);
        toast.success('Expense deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete expense');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      expense_date: '',
      expense_from: '',
      expense_no: '',
      supplier_vat_number: '',
      expense_type: '',
      amount: '',
      vat: 0,
      total_with_vat: 0,
      description: '',
      project_id: '',
      photo_url: '',
      payment_source: 'cash',
      petty_cash_fund_id: '',
      cash_account_id: '',
      employee_id: ''
    });
    setEditingExpense(null);
  };

  // Phase 7.x · P0 — currently selected fund (for balance hint + filter).
  const selectedFund = pettyCashFunds.find(f => f.id === formData.petty_cash_fund_id);
  // Auto-filter funds by chosen employee/project (custodian model). If
  // neither filter applies, show every active fund — same UX as Bank.
  const eligibleFunds = pettyCashFunds.filter(f => {
    if (formData.employee_id && f.employee_id && f.employee_id !== formData.employee_id) return false;
    if (formData.project_id && f.project_id && f.project_id !== formData.project_id) return false;
    return true;
  });
  const total = parseFloat(formData.total_with_vat || 0);
  const insufficient = formData.payment_source === 'petty_cash' && selectedFund
    && total > (selectedFund.current_balance || 0);
  const overLimit = formData.payment_source === 'petty_cash' && selectedFund
    && selectedFund.max_transaction_limit
    && total > selectedFund.max_transaction_limit;

  const totalExpenses = expenses.reduce(
    (sum, exp) => sum + (Number(exp?.total_with_vat) || 0),
    0,
  );

  if (loading) {
    return <div className="text-center py-12">Loading expenses...</div>;
  }

  return (
    <div data-testid="expenses-page">
      <div className="page-header">
        <h2 className="page-title">Expenses</h2>
        <button
          data-testid="add-expense-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Expense
        </button>
      </div>

      {/* Legacy bank-source repair banner */}
      {bankAudit && bankAudit.count > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6" dir="rtl" data-testid="bank-source-repair-banner">
          <p className="font-semibold text-amber-800 mb-1">
            ⚠️ {bankAudit.count} مصروف قديم بمصدر دفع «بنك» ({bankAudit.total.toLocaleString()} ريال) خُصم خطأً من حساب العهدة
          </p>
          <p className="text-sm text-amber-700 mb-3">
            اختر حساب البنك الصحيح وسيتم تلقائياً: استرجاع المبالغ من العهدة، خصمها من البنك، وإظهارها في كشف حركات البنك.
          </p>
          <div className="flex gap-2 flex-wrap items-center">
            <select
              data-testid="repair-bank-select"
              value={repairBankId}
              onChange={(e) => setRepairBankId(e.target.value)}
              className="form-input max-w-xs"
            >
              <option value="">اختر حساب البنك…</option>
              {bankAccounts.map(b => (
                <option key={b.id} value={b.id}>{b.bank_name} - {b.account_number}</option>
              ))}
            </select>
            <button
              data-testid="repair-bank-btn"
              onClick={handleBankSourceRepair}
              disabled={repairing || !repairBankId}
              className="btn-primary py-2 px-4 disabled:opacity-50"
            >
              {repairing ? 'جارٍ التصحيح…' : 'تصحيح الآن'}
            </button>
          </div>
        </div>
      )}

      <div className="stat-card mb-6">
        <div className="stat-icon red">
          <TrendingUp className="w-6 h-6" />
        </div>
        <h3 className="text-gray-600 text-sm font-medium mb-1">Total Expenses (with VAT)</h3>
        <p className="text-3xl font-bold text-gray-900">{totalExpenses.toLocaleString()} SAR</p>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Expense From</th>
              <th>Expense No</th>
              <th>Type</th>
              <th>Source</th>
              <th>Amount</th>
              <th>VAT</th>
              <th>Total with VAT</th>
              <th>Description</th>
              <th>Project</th>
              <th>User</th>
              <th>Photo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length > 0 ? (
              expenses.map((expense) => (
                <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                  <td>{expense.expense_date ? new Date(expense.expense_date).toLocaleDateString() : '—'}</td>
                  <td className="font-semibold">{expense.expense_from || '—'}</td>
                  <td className="font-semibold text-purple-600">{expense.expense_no || '—'}</td>
                  <td>
                    <span className="status-badge planning">{expense.expense_type || '—'}</span>
                  </td>
                  <td>
                    {expense.payment_source === 'petty_cash' ? (
                      <span
                        data-testid={`expense-source-petty-${expense.id}`}
                        title={expense.petty_cash_fund_name || 'Petty Cash'}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium"
                      >
                        <Wallet className="w-3 h-3" />
                        {expense.petty_cash_fund_name ? expense.petty_cash_fund_name : 'Petty Cash'}
                      </span>
                    ) : expense.payment_source === 'cash' ? (
                      <span
                        data-testid={`expense-source-cash-${expense.id}`}
                        title={expense.cash_account_name || 'Cash'}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium"
                      >
                        <DollarSign className="w-3 h-3" />
                        {expense.cash_account_name || 'Cash'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium">
                        <Building2 className="w-3 h-3" />
                        Bank
                      </span>
                    )}
                  </td>
                  <td>{(Number(expense.amount) || 0).toLocaleString()} SAR</td>
                  <td className="text-orange-600">{(Number(expense.vat) || 0).toLocaleString()} SAR</td>
                  <td className="font-bold text-red-600">{(Number(expense.total_with_vat) || 0).toLocaleString()} SAR</td>
                  <td className="max-w-xs truncate">{expense.description || ''}</td>
                  <td>{expense.project_name || 'N/A'}</td>
                  <td>{expense.user_name || '—'}</td>
                  <td>
                    {expense.photo_url ? (
                      <button
                        onClick={() => handleViewImage(expense.photo_url)}
                        className="btn-icon text-blue-600 hover:bg-blue-50"
                        title="View Photo"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        data-testid={`edit-expense-${expense.id}`}
                        onClick={() => handleEdit(expense)}
                        className="btn-icon edit"
                        title="Edit Expense"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-expense-${expense.id}`}
                        onClick={() => handleDelete(expense.id)}
                        className="btn-icon delete"
                        title="Delete Expense"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center py-12 text-gray-500">
                  No expenses found. Add your first expense!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal — hidden (display:none) while the receipt camera is open
          so the camera scanner gets the full screen, then reappears with
          the captured PDF already attached so the user can fill the rest. */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => { setShowModal(false); resetForm(); }}
          style={cameraOpen ? { display: 'none' } : undefined}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingExpense ? 'Edit Expense' : 'New Expense'}</h3>
              <button
                data-testid="close-modal-button"
                onClick={() => { setShowModal(false); resetForm(); }}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* 1. Expense Date */}
              <div className="form-group">
                <label className="form-label">Expense Date *</label>
                <input
                  data-testid="expense-date-input"
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              {/* 2. Expense From */}
              <div className="form-group">
                <label className="form-label">Expense From *</label>
                <input
                  data-testid="expense-from-input"
                  type="text"
                  value={formData.expense_from}
                  onChange={(e) => setFormData({ ...formData, expense_from: e.target.value })}
                  onBlur={(e) => autofillFromVendor(e.target.value)}
                  list="expense-from-suggestions"
                  className="form-input"
                  placeholder="Supplier or vendor name"
                  required
                />
                {/* Native datalist — offers past vendor names as suggestions.
                    Selecting one instantly triggers the onBlur autofill above. */}
                <datalist id="expense-from-suggestions">
                  {[...new Set(expenses.map(x => x.expense_from).filter(Boolean))].map(name => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </div>

              {/* 3. Expense No (Invoice number from supplier) */}
              <div className="form-group">
                <label className="form-label">Expense No (Invoice Number) *</label>
                <input
                  data-testid="expense-no-input"
                  type="text"
                  value={formData.expense_no}
                  onChange={(e) => setFormData({ ...formData, expense_no: e.target.value })}
                  className="form-input"
                  placeholder="Invoice/Receipt number"
                  required
                />
              </div>

              {/* 3b. Supplier VAT Number (Saudi ZATCA - 15 digits) */}
              <div className="form-group">
                <label className="form-label">VAT Number (الرقم الضريبي للمورد)</label>
                <input
                  data-testid="expense-vat-number-input"
                  type="text"
                  value={formData.supplier_vat_number}
                  onChange={(e) => setFormData({ ...formData, supplier_vat_number: e.target.value })}
                  className="form-input"
                  placeholder="3XXXXXXXXXXXXXX (15 digits)"
                  maxLength="15"
                  pattern="[0-9]{15}"
                />
              </div>

              {/* 4. Expense Type */}
              <div className="form-group">
                <label className="form-label">Expense Type *</label>
                {/* AI suggestion banner — Autopilot Confidence Bridge.
                    Uses /api/intelligence/suggest with the live vendor +
                    description as context. Shows a one-click Accept pill
                    when the rule/LLM engine returns a tier ≥ suggest. */}
                <ExpenseTypeAISuggestion
                  vendor={formData.expense_from}
                  description={formData.description}
                  currentValue={formData.expense_type}
                  onAccept={(v) => setFormData({ ...formData, expense_type: v })}
                />
                <select
                  data-testid="expense-type-select"
                  value={formData.expense_type}
                  onChange={(e) => setFormData({ ...formData, expense_type: e.target.value })}
                  className="form-select"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Materials">Materials</option>
                  <option value="Labor">Labor</option>
                  <option value="Equipment">Equipment</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Legal">Legal</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Office Supplies">Office Supplies</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* 5. Amount */}
              <div className="form-group">
                <label className="form-label">Amount (SAR) *</label>
                <input
                  data-testid="expense-amount-input"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="form-input"
                  placeholder="0.00"
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              {/* 6. VAT */}
              <div className="form-group">
                <label className="form-label">VAT (SAR)</label>
                <input
                  data-testid="expense-vat-input"
                  type="number"
                  value={formData.vat}
                  onChange={(e) => handleVatChange(e.target.value)}
                  className="form-input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* 7. Total with VAT */}
              <div className="form-group">
                <label className="form-label">Total with VAT</label>
                <input
                  data-testid="expense-total-display"
                  type="text"
                  value={`${formData.total_with_vat.toFixed(2)} SAR`}
                  className="form-input bg-gray-100 font-bold text-lg"
                  readOnly
                  disabled
                />
              </div>

              {/* 8. Description */}
              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea
                  data-testid="expense-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Expense details"
                  required
                />
              </div>

              {/* 9. Project Name */}
              <div className="form-group">
                <label className="form-label">Project Name (Optional)</label>
                <select
                  data-testid="expense-project-select"
                  value={formData.project_id || ''}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value || null })}
                  className="form-select"
                >
                  <option value="" data-testid="expense-project-no-project">— No Project (General Expense — debited to Cash Expenses 11150) —</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {!formData.project_id && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="expense-no-project-hint">
                    This expense will <strong>not</strong> be allocated to any project. It will hit the general expense ledger only.
                  </p>
                )}
              </div>

              {/* Payment Source toggle (Jun 2026 — 'Bank' removed).
                  Bank-paid expenses now go through Government & Utility
                  Payments → General Expense so they hit the real bank
                  GL account and mirror into the bank ledger.
                  Cash          ⇒ credit a dedicated cash GL account.
                  Petty Cash    ⇒ deduct fund balance. */}
              <div className="form-group">
                <label className="form-label">Payment Source *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="payment-source-cash-btn"
                    onClick={() => {
                      // Auto-pick the canonical 11150 · Cash Expenses
                      // account so the user doesn't have to select it
                      // manually.  If the tenant hasn't been seeded
                      // with 11150 yet the picker below stays visible
                      // as a graceful fallback.
                      const def = findDefaultCashExpenseAccount();
                      setFormData({
                        ...formData,
                        payment_source: 'cash',
                        petty_cash_fund_id: '',
                        employee_id: '',
                        cash_account_id: def ? def.id : '',
                      });
                    }}
                    className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 transition-all ${
                      formData.payment_source === 'cash'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <DollarSign className="w-4 h-4" />
                    Cash
                  </button>
                  <button
                    type="button"
                    data-testid="payment-source-petty-cash-btn"
                    onClick={() => setFormData({ ...formData, payment_source: 'petty_cash', cash_account_id: '' })}
                    className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 transition-all ${
                      formData.payment_source === 'petty_cash'
                        ? 'border-amber-500 bg-amber-50 text-amber-700 font-semibold'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Wallet className="w-4 h-4" />
                    Petty Cash
                  </button>
                </div>
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-2" dir="rtl" data-testid="bank-source-moved-hint">
                  💡 المصروفات المدفوعة من <strong>البنك</strong> تُسجَّل الآن من صفحة
                  «المدفوعات الحكومية والمرافق → مصروفات عامة» حتى تُخصم من حساب البنك
                  الصحيح وتظهر في كشف حركات البنك.
                </p>
              </div>

              {/* Cash account picker — only when source = cash. */}
              {formData.payment_source === 'cash' && (() => {
                const defaultAcct = findDefaultCashExpenseAccount();
                // If the canonical Cash Expenses account exists AND it's
                // already selected, show a compact confirmation card
                // rather than the full picker (per Feb 2026 UX spec:
                // "clicking Cash should not require a second pick").
                const autoSelected =
                  defaultAcct && formData.cash_account_id === defaultAcct.id;
                if (autoSelected) {
                  return (
                    <div
                      data-testid="cash-account-auto"
                      className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/60 flex items-center gap-2"
                    >
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <div className="text-sm text-emerald-800">
                        <span className="font-semibold">
                          {defaultAcct.account_number} · {defaultAcct.account_name}
                        </span>{' '}
                        <span className="text-emerald-700">
                          — يُخصم منه المصروف النقدي تلقائياً.
                        </span>
                      </div>
                    </div>
                  );
                }
                return (
                <div data-testid="cash-account-picker" className="space-y-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50/50">
                  <div className="form-group">
                    <label className="form-label flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                      Cash Account * (حساب النقدية · CR)
                    </label>
                    <select
                      data-testid="expense-cash-account-select"
                      value={formData.cash_account_id}
                      onChange={(e) => setFormData({ ...formData, cash_account_id: e.target.value })}
                      className="form-select"
                      required={formData.payment_source === 'cash'}
                    >
                      <option value="">— Select cash account —</option>
                      {cashAccounts.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.account_number ? `${a.account_number} · ` : ''}{a.account_name}
                          {a.name_arabic ? ` — ${a.name_arabic}` : ''}
                        </option>
                      ))}
                    </select>
                    {cashAccounts.length === 0 && (
                      <p className="text-xs text-rose-600 mt-1">
                        لا توجد حسابات نقدية معرَّفة. أنشئ حساباً في شجرة الحسابات (مثل 11150 · Cash Expenses) وضع عليه علامة “حساب نقدي”.
                      </p>
                    )}
                  </div>
                </div>
                );
              })()}

              {/* Petty cash custody picker — only when source = petty_cash. */}
              {formData.payment_source === 'petty_cash' && (
                <div data-testid="petty-cash-picker" className="space-y-3 p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                  <div className="form-group">
                    <label className="form-label flex items-center gap-1">
                      <Users className="w-3.5 h-3.5 text-blue-500" />
                      Custodian (Employee) — Optional
                    </label>
                    <select
                      data-testid="expense-employee-select"
                      value={formData.employee_id}
                      onChange={(e) => setFormData({ ...formData, employee_id: e.target.value, petty_cash_fund_id: '' })}
                      className="form-select"
                    >
                      <option value="">— Any custodian —</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center gap-1">
                      <Wallet className="w-3.5 h-3.5 text-amber-600" />
                      Petty Cash Fund *
                    </label>
                    <select
                      data-testid="expense-fund-select"
                      value={formData.petty_cash_fund_id}
                      onChange={(e) => setFormData({ ...formData, petty_cash_fund_id: e.target.value })}
                      className="form-select"
                      required={formData.payment_source === 'petty_cash'}
                    >
                      <option value="">Select Fund</option>
                      {eligibleFunds.map(f => (
                        <option key={f.id} value={f.id}>
                          {f.name} — Balance: {(f.current_balance || 0).toLocaleString()} SAR
                          {f.employee_name ? ` · ${f.employee_name}` : ''}
                          {f.project_name ? ` · ${f.project_name}` : ''}
                        </option>
                      ))}
                    </select>
                    {eligibleFunds.length === 0 && (
                      <p className="text-xs text-rose-600 mt-1">
                        لا توجد عهد فعالة تطابق هذا الموظف/المشروع — أنشئ صندوقاً من صفحة Petty Cash أولاً.
                      </p>
                    )}
                  </div>

                  {selectedFund && (
                    <div data-testid="fund-balance-hint" className="text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">رصيد العهدة:</span>
                        <span className="font-semibold text-amber-700">
                          {(selectedFund.current_balance || 0).toLocaleString()} SAR
                        </span>
                      </div>
                      {selectedFund.max_transaction_limit && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">الحد الأقصى للحركة:</span>
                          <span className="font-semibold text-amber-700">
                            {selectedFund.max_transaction_limit.toLocaleString()} SAR
                          </span>
                        </div>
                      )}
                      {insufficient && (
                        <p data-testid="insufficient-balance-warning" className="text-rose-600 font-medium mt-1">
                          ⚠ الرصيد غير كافٍ — المطلوب {total.toLocaleString()} SAR.
                        </p>
                      )}
                      {overLimit && (
                        <p data-testid="over-limit-warning" className="text-rose-600 font-medium mt-1">
                          ⚠ يتجاوز الحد الأقصى للحركة في هذه العهدة.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 10. PDF Attachment */}
              <div className="form-group">
                <label className="form-label">Attached PDF File (Optional)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-secondary flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Attach PDF File
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCameraOpen(true)}
                    disabled={uploading}
                    data-testid="expense-camera-open-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-lg font-medium"
                  >
                    <Camera className="w-4 h-4" />
                    التقط صورة الإيصال
                  </button>
                  {formData.photo_url && (
                    <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">File attached</span>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, photo_url: '' })}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                {formData.photo_url && (
                  <div className="mt-3">
                    {formData.photo_url.toLowerCase().endsWith('.pdf') ? (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <FileText className="w-8 h-8 text-red-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">PDF File Attached</p>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${formData.photo_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Click to view PDF
                          </a>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${formData.photo_url}`}
                        alt="Receipt preview"
                        className="max-h-32 rounded-lg border border-gray-200 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* 11. User - Auto from current user (shown as info) */}
              <div className="bg-green-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-green-800">
                  <strong>User:</strong> Will be set to your name automatically
                </p>
              </div>

              {/* Petty Cash blocking-reason hint — keeps the user from
                  silently staring at a disabled "Add Expense" button. */}
              {formData.payment_source === 'petty_cash' && (
                !formData.petty_cash_fund_id || insufficient || overLimit
              ) && (
                <div
                  data-testid="petty-cash-blocker-hint"
                  className="text-sm font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mt-2"
                >
                  {!formData.petty_cash_fund_id
                    ? (eligibleFunds.length === 0
                        ? '⚠ لا توجد عهدة فعالة متاحة — أنشئ صندوقاً من صفحة Petty Cash أولاً.'
                        : '⚠ اختر صندوق العهدة (Petty Cash Fund) قبل الحفظ.')
                    : insufficient
                      ? '⚠ الرصيد غير كافٍ في العهدة المختارة — اشحن العهدة أو اختر مصدر دفع آخر.'
                      : '⚠ المبلغ يتجاوز الحد الأقصى للحركة في هذه العهدة.'}
                </div>
              )}

              {/* Cash payment blocker — mirrors the petty-cash hint below. */}
              {formData.payment_source === 'cash' && !formData.cash_account_id && (
                <div
                  data-testid="cash-account-blocker-hint"
                  className="text-sm font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mt-2"
                >
                  {cashAccounts.length === 0
                    ? '⚠ لا توجد حسابات نقدية معرَّفة — أنشئ حساباً (مثل 11150 · Cash Expenses) وضع عليه علامة “حساب نقدي”.'
                    : '⚠ اختر حساب النقدية (Cash Account) قبل الحفظ.'}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-expense-button"
                  type="submit"
                  disabled={
                    (formData.payment_source === 'petty_cash' &&
                      (!formData.petty_cash_fund_id || insufficient || overLimit)) ||
                    (formData.payment_source === 'cash' && !formData.cash_account_id)
                  }
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingExpense ? 'Update Expense' : 'Add Expense'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Attached File</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex justify-center">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${selectedImage}`}
                alt="Attachment"
                className="max-w-full max-h-[70vh] rounded-lg object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<p class="text-gray-500">Unable to display file. <a href="' + process.env.REACT_APP_BACKEND_URL + selectedImage + '" target="_blank" class="text-blue-600 underline">Click here to download</a></p>';
                }}
              />
            </div>
          </div>
        </div>
      )}

      <ExpenseCameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCaptured={(photoUrl) => setFormData(fd => ({ ...fd, photo_url: photoUrl }))}
        onExtracted={(ocr) => setFormData(fd => {
          const next = { ...fd };
          // Only fill fields that are still empty — never overwrite the
          // user's own input. This applies even when they confirmed the
          // OCR preview, because the user might already have typed.
          if (ocr.expense_date && !fd.expense_date) next.expense_date = ocr.expense_date;
          if (ocr.vendor       && !fd.expense_from) next.expense_from = ocr.vendor;
          if (ocr.invoice_no   && !fd.expense_no)   next.expense_no   = ocr.invoice_no;
          if (ocr.description  && !fd.description)  next.description  = ocr.description;
          if (ocr.amount != null && (!fd.amount || parseFloat(fd.amount) === 0)) {
            next.amount = String(ocr.amount);
          }
          if (ocr.vat_amount != null && (!fd.vat || parseFloat(fd.vat) === 0)) {
            next.vat = String(ocr.vat_amount);
          }
          const a = parseFloat(next.amount) || 0;
          const v = parseFloat(next.vat) || 0;
          next.total_with_vat = a + v;
          return next;
        })}
      />
    </div>
  );
};

export default Expenses;